import { Link } from "react-router-dom";
import {
  ArrowRight, Hotel, Landmark, Building2, Wrench, Flame, Shield, Sparkles,
  MapPin, ClipboardCheck, HardHat, KeyRound, FileCheck2, Layers,
} from "lucide-react";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FEATURES = [
  {
    icon: Layers,
    title: "Vertically integrated",
    body: "We manage the project from land sourcing to Certificate of Occupancy and beyond — reducing the coordination overhead of juggling ten separate consultants.",
  },
  {
    icon: Building2,
    title: "80-120 line-item permit matrix",
    body: "A full-service hotel ground-up expands to 80-120 discrete permits, approvals, and inspections. We track every one in a single closeout matrix.",
  },
];

// Core permit categories that show up on every hotel ground-up
const CORE_PERMITS = [
  {
    id: "zoning",
    icon: Landmark,
    title: "Zoning & Land Use Permits",
    body: "Rezoning, PUDs, Conditional/Special Use Permits, Development Agreements, and variances (setback, height, parking, landscape). Site Plan Approval routes through Planning, Engineering, Fire, Utilities, Traffic, Stormwater, Landscaping, Architecture and Accessibility. Includes subdivision/platting, replats, and easement dedications.",
  },
  {
    id: "building",
    icon: Building2,
    title: "Building Permits (Structural & Envelope)",
    body: "The largest permit package. Architectural, structural, civil, energy, accessibility, and special-inspection plan review. Covers foundations, structure, exterior walls, roofing, interior framing, building envelope, and occupancy classification.",
  },
  {
    id: "mep",
    icon: Wrench,
    title: "MEP Permits — Mechanical, Electrical, Plumbing",
    body: "Mechanical: HVAC, exhaust, kitchen hood, makeup air, smoke control, RTUs, boilers. Electrical: service, panels, generators, transformers, lighting, emergency power, EV chargers, fire-alarm power. Plumbing: water, sewer, gas, domestic water, grease interceptor, water heaters, roof drains.",
  },
  {
    id: "fire",
    icon: Flame,
    title: "Fire & Life-Safety Permits",
    body: "Fire sprinklers, alarm, pump, standpipes, kitchen-hood suppression, and ERRCS/BDA where required. Fire Marshal reviews occupancy, egress, exit signage, fire ratings, fire walls, smoke compartments, stair enclosures, fire lanes, hydrant coverage and Knox Box placement.",
  },
  {
    id: "specialty",
    icon: Shield,
    title: "Specialty Permits (Cranes, Elevators & More)",
    body: "Elevator installation and state elevator inspections leading to Certificate of Operation. Tower & mobile crane permits with street closure coordination. Demolition, sign, pool, generator, fuel tank, kitchen hood, and low-voltage (security/CCTV/data/access control) permits — each often issued separately.",
  },
];

// Full 13-phase roadmap
const ROADMAP = [
  {
    id: "phase-1",
    tag: "Phase 1",
    title: "Site Selection & Due Diligence",
    icon: MapPin,
    bullets: [
      "Land acquisition, purchase agreement, ALTA survey",
      "Environmental Review (ESA Phase I / II where triggered)",
      "Geotechnical, utility availability, traffic, floodplain, wetlands",
      "Feasibility study + hotel-flag selection",
    ],
    deliverables: [
      "Due Diligence Report",
      "Feasibility Report",
      "Preliminary Site Plan",
      "Utility Capacity Letter",
    ],
    agencies: ["Planning", "Utility providers", "Environmental agencies", "DOT", "County GIS"],
  },
  {
    id: "phase-2",
    tag: "Phase 2",
    title: "Entitlements & Land Use",
    icon: Landmark,
    bullets: [
      "Zoning verification: use, parking, height, FAR, open space, setbacks, landscaping",
      "Rezoning, PD, CUP, SUP, PUD, Development Agreements, variances",
      "Site Plan Approval — Planning, Engineering, Fire, Utilities, Traffic, Stormwater, Landscape, Architecture, ADA",
      "Subdivision & platting (preliminary, final, replat, lot combination, easement dedications) where required",
    ],
  },
  {
    id: "phase-3",
    tag: "Phase 3",
    title: "Civil Engineering Permits",
    icon: Layers,
    bullets: [
      "Civil plan review: grading, drainage, stormwater, utility, water/sewer, detention, erosion control",
      "Public works: utility, storm sewer, sanitary sewer, water main, sidewalk, curb cuts, driveways, street improvements",
      "DOT: driveway permits, highway access, signal mods, decel lanes, right-of-way work",
      "SWPPP + NOI + construction stormwater permit",
    ],
  },
  {
    id: "phase-4",
    tag: "Phase 4",
    title: "Building Permit Plan Review",
    icon: ClipboardCheck,
    bullets: [
      "Reviewers: Building, Structural, Mechanical, Electrical, Plumbing, Fire, Accessibility, Energy, Green Building",
      "Required plan sets: Architectural, Structural, Civil, MEP, Fire Protection, Life Safety, ADA, Energy, Special Inspections, Geotechnical",
    ],
  },
  {
    id: "phase-5",
    tag: "Phase 5",
    title: "Commercial Building Permit",
    icon: Building2,
    bullets: [
      "Primary construction permit",
      "Foundations, structure, exterior walls, roofing, interior framing, envelope",
      "Accessibility + occupancy classification finalized",
    ],
  },
  {
    id: "phase-6",
    tag: "Phase 6",
    title: "MEP Permits",
    icon: Wrench,
    bullets: [
      "Mechanical: HVAC, exhaust, kitchen hood, makeup air, smoke control, RTUs, boilers",
      "Electrical: service, panels, generators, transformers, lighting, emergency power, EV chargers, fire-alarm power",
      "Plumbing: water, sewer, gas, domestic water, grease interceptor, water heaters, roof drains",
      "May be issued under the building permit or separately, depending on jurisdiction",
    ],
  },
  {
    id: "phase-7",
    tag: "Phase 7",
    title: "Fire & Life Safety",
    icon: Flame,
    bullets: [
      "Fire sprinklers, alarm, pump, standpipes, hood suppression, ERRCS/BDA",
      "Occupancy, egress, exit signs, fire ratings, fire walls, smoke compartments, stair enclosures",
      "Fire lanes, hydrant coverage, Knox Box",
    ],
  },
  {
    id: "phase-8",
    tag: "Phase 8",
    title: "Specialty Permits",
    icon: Shield,
    bullets: [
      "Elevator: installation, state inspections, Certificate of Operation",
      "Cranes: tower/mobile + street-closure coordination",
      "Demolition (if existing structure removed), signage (monument/building/wayfinding), pool",
      "Generator, fuel tank, kitchen hood, low-voltage (security/CCTV/data/access control)",
    ],
  },
  {
    id: "phase-9",
    tag: "Phase 9",
    title: "Deferred Submittals",
    icon: FileCheck2,
    bullets: [
      "Fire sprinklers, fire alarm",
      "Steel connections, trusses",
      "Curtain wall, elevators, kitchen equipment, prefabricated stairs",
    ],
  },
  {
    id: "phase-10",
    tag: "Phase 10",
    title: "Early Start / Early Work",
    icon: HardHat,
    bullets: [
      "Mass grading, site clearing",
      "Utilities, foundations",
      "Shoring, retaining walls",
      "Available in many jurisdictions before the full building permit is issued",
    ],
  },
  {
    id: "phase-11",
    tag: "Phase 11",
    title: "Construction Inspections",
    icon: ClipboardCheck,
    bullets: [
      "Footings, foundation, underground plumbing / electrical / fire line",
      "Slab, framing, MEP rough-in, insulation, drywall, ceiling",
      "Fire sprinkler, fire alarm, elevator, kitchen hood, accessibility",
      "Final Building, Final Fire, Final Health (if applicable)",
    ],
  },
  {
    id: "phase-12",
    tag: "Phase 12",
    title: "Certificate of Occupancy (CO)",
    icon: KeyRound,
    bullets: [
      "Final inspections approved · fire · building · elevator · health",
      "Site completion or approved temporary conditions",
      "Address verification, as-built documentation",
      "Special-inspection closeout, deferred submittals complete",
      "TCO or full CO issued",
    ],
  },
  {
    id: "phase-13",
    tag: "Phase 13",
    title: "Operational Licenses",
    icon: Sparkles,
    bullets: [
      "Business license, hotel/motel license (where required)",
      "Sales tax + hotel occupancy tax registration, Certificate of Registration",
      "Food service permit, liquor license, health department license",
      "Pool operating permit, elevator operating certificate, fire operational permit, alarm permit",
      "Music license (ASCAP / BMI / SESAC) if applicable",
    ],
  },
];

export default function Hotels() {
  return (
    <div className="min-h-screen bg-white" data-testid="hotels-page">
      <Header />

      {/* HERO */}
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 mb-6" data-testid="hotels-eyebrow">
              <Hotel className="w-3 h-3 text-emerald-700" />
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-700">
                Hospitality · Ground-Up Development
              </span>
            </div>
            <h1 className="font-outfit text-4xl md:text-6xl lg:text-7xl font-bold text-slate-900 tracking-tight leading-[1.03]" data-testid="hotels-title">
              Hotel Permitting.{" "}
              <span className="text-emerald-700">From land to ribbon-cut.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-slate-600 max-w-3xl leading-relaxed" data-testid="hotels-intro">
              Coast To Coast Permitting delivers <strong className="text-slate-900">end-to-end hotel
              development services</strong> — assisting with land sourcing, environmental feasibility,
              coordinating with entitlements submissions, planning, construction and hotel permit
              operations. We&rsquo;re vertically integrated: we help project manage all things
              permitting and assist general contractors to get the job done, not just push paper
              between them.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-slate-900 hover:bg-slate-800 text-white h-12 px-8" data-testid="hotels-hero-cta">
                <Link to="/hire?service=hotels&utm_source=hotels_page_hero">Discuss a hotel project<ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-12 px-6" data-testid="hotels-hero-secondary">
                <a href="#roadmap">See the 13-phase roadmap</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE BULLETS */}
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="hotels-features">
            {FEATURES.map((f) => {
              const Ico = f.icon;
              const slug = f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
              return (
                <div key={f.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-7 hover:bg-white hover:border-emerald-300 hover:shadow-md transition-all" data-testid={`hotel-feature-${slug}`}>
                  <div className="w-11 h-11 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center mb-4">
                    <Ico className="w-5 h-5" />
                  </div>
                  <h3 className="font-outfit text-lg font-bold text-slate-900 mb-2 leading-tight">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CORE PERMITS */}
      <section className="border-b border-slate-200 bg-slate-50" data-testid="hotels-permits-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-700 mb-3">Permit types</div>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
              Five permit tracks every hotel ground-up hits.
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              A typical hotel expands to <strong className="text-slate-900">80–120 discrete permits, approvals, and inspections</strong> across
              the life of the project. They roll up under these five categories — and we manage each one under a single point of accountability.
            </p>
          </div>
          <div className="mt-10 max-w-4xl">
            <Accordion type="single" collapsible className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100" data-testid="hotels-permits-accordion" defaultValue="zoning">
              {CORE_PERMITS.map((item) => {
                const Ico = item.icon;
                return (
                  <AccordionItem key={item.id} value={item.id} className="border-0" data-testid={`permit-accordion-${item.id}`}>
                    <AccordionTrigger className="px-5 py-4 text-left hover:no-underline hover:bg-slate-50 [&[data-state=open]]:bg-emerald-50/40">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="flex-none w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                          <Ico className="w-4 h-4" />
                        </div>
                        <span className="font-outfit text-base font-bold text-slate-900">{item.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">
                      <div className="pl-11">{item.body}</div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        </div>
      </section>

      {/* 13-PHASE ROADMAP */}
      <section id="roadmap" className="border-b border-slate-200 bg-white" data-testid="hotels-roadmap-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-700 mb-3">Complete roadmap</div>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
              Land acquisition → Certificate of Occupancy → business license.
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              A hotel ground-up follows the same permitting spine as any commercial building, but the
              hospitality overlay (health, pool, elevator, food service, liquor, flag design standards)
              turns it into 13 coordinated phases. Here&rsquo;s how we run it.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="hotels-roadmap-grid">
            {ROADMAP.map((phase, i) => {
              const Ico = phase.icon;
              return (
                <div key={phase.id} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 hover:bg-white hover:border-emerald-300 hover:shadow-md transition-all" data-testid={`phase-card-${phase.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-none">
                        <Ico className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-700">{phase.tag}</div>
                        <h3 className="font-outfit text-base font-bold text-slate-900 leading-tight">{phase.title}</h3>
                      </div>
                    </div>
                    <span className="text-[10px] font-mono text-slate-400 flex-none">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <ul className="space-y-1.5">
                    {phase.bullets.map((b, idx) => (
                      <li key={idx} className="flex gap-2 text-[13px] text-slate-600 leading-snug">
                        <span className="text-emerald-600 mt-1 flex-none">▪</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  {phase.deliverables && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Deliverables</div>
                      <div className="flex flex-wrap gap-1.5">
                        {phase.deliverables.map((d) => (
                          <span key={d} className="text-[11px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">{d}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {phase.agencies && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Typical agencies</div>
                      <div className="text-[11px] text-slate-600">{phase.agencies.join(" · ")}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="mt-10 text-sm md:text-base text-slate-700 leading-relaxed max-w-3xl" data-testid="hotels-roadmap-closing">
            For a national hospitality permitting program, this workflow typically expands to
            <strong className="text-slate-900"> 80–120 individual permit, approval, inspection, and closeout items</strong>,
            depending on jurisdiction and hotel flag. We manage all of them under a single
            permitting matrix per property — with the same matrix reused as a due-diligence
            checklist across your portfolio.
          </p>
        </div>
      </section>

      {/* VERTICALLY-INTEGRATED PITCH */}
      <section className="bg-slate-900 text-white border-b border-slate-800" data-testid="hotels-integrated-section">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-400 mb-3">One team, one accountability</div>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight">
              Vertically integrated hotel development.
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed">
              CTC Permits manages your project from land sourcing to Certificate of Occupancy —
              rather than handing you off between a broker, a civil, an entitlements consultant,
              an architect, an expediter, a fire consultant, and a hospitality-flag liaison.
              We work directly with your General Contractor to keep the field-side and the paper-side
              in lockstep.
            </p>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="hotels-integrated-stages">
              {[
                "Land sourcing",
                "Feasibility",
                "Entitlements",
                "Planning & design",
                "Construction",
                "Hotel operations",
              ].map((stage) => (
                <div key={stage} className="bg-slate-800/60 border border-slate-700 rounded-lg px-4 py-3 text-sm">
                  {stage}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-700 mb-6">
              <Hotel className="w-6 h-6" />
            </div>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
              Have a hotel project on the drawing board?
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Tell us the market, the flag, and the timeline. We&rsquo;ll come back within 24 hours
              with a permitting matrix, a fee-of-record estimate, and a real person assigned to
              the engagement.
            </p>
            <div className="mt-9 flex flex-wrap gap-3 justify-center">
              <Button asChild size="lg" className="bg-slate-900 hover:bg-slate-800 text-white h-12 px-8" data-testid="hotels-final-cta">
                <Link to="/hire?service=hotels&utm_source=hotels_page_footer">Discuss a hotel project<ArrowRight className="ml-2 w-4 h-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-12 px-6" data-testid="hotels-dd-cta">
                <Link to="/due-diligence?project_type=hotel&utm_source=hotels_page_footer">Order a hotel DD report</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
